/**
 * Admin — Revenue Calculation, the region shell (built by card 1241, extracted by 1243).
 *
 * Three tiny pure presentational pieces shared by all four regions of the page. They live
 * here rather than in the page because every card from 1243 on adds a region body, and the
 * page has a hard 300-line ceiling it had already reached twice.
 *
 * 🔑 `Section` renders EXACTLY ONE of three branches: loading → children → empty, via
 * `children ?? <SectionEmpty>`. ⛔ Callers must pass ONE child expression or `null` — two
 * sibling JSX expressions make `children` an ARRAY, which is never nullish, and the empty
 * state silently stops rendering on every region at once.
 */

import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/** Pure. A skeleton block of `rows` shimmer bars. */
export const SectionLoading = ({ rows }) => (
  <div className={styles.loading} data-testid="region-loading">
    <p className={styles.loadingLabel}>Loading</p>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className={styles.skeletonRow} />
    ))}
  </div>
);

/** Pure. The settled "nothing here" state — never a zero, always an explanation. */
export const SectionEmpty = ({ title, note }) => (
  <div className={styles.empty} data-testid="region-empty">
    <p className={styles.emptyTitle}>{title}</p>
    <p className={styles.emptyNote}>{note}</p>
  </div>
);

/** Pure. One region card. */
const Section = ({ region, isLoading, children }) => (
  <section
    className={styles.section}
    id={region.id}
    data-testid={`region-${region.id}`}
    aria-busy={isLoading}
    aria-labelledby={`${region.id}-label`}
  >
    <div className={styles.sectionHead}>
      <h2 className={styles.sectionLabel} id={`${region.id}-label`}>{region.label}</h2>
      <p className={styles.sectionNote}>{region.note}</p>
    </div>
    {isLoading
      ? <SectionLoading rows={region.skeletonRows} />
      : (children ?? <SectionEmpty title={region.emptyTitle} note={region.emptyNote} />)}
  </section>
);

export default Section;
